import Interweave from 'interweave'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { convertMdToHtml } from '../markdown'
import Link from '../../Link'
import Section from '../../Section'
import {
  getCodeFence,
  getFindingTitle,
  getMarkdownLanguage,
  getOrderedFindings,
  getReproductionTestId,
  getReproductionTestLanguage,
  hasReproductionTest,
} from '../utils'
import { FINDING_HTML_ID_ATTRIBUTE } from '../../../constants'

const TestContainer = styled.article`
  border: 1px solid #d8dee9;
  border-radius: 6px;
  overflow: hidden;

  & + & {
    margin-top: 24px;
  }
`

const SectionDescription = styled.p`
  max-width: 920px;
  margin: 0;
  color: #4b5563;
  font-size: 16px;
  line-height: 24px;
`

const FindingIdentifier = styled(Link)`
  margin-right: 4px;
  font-size: 21px;
  font-weight: 700;
  line-height: 24px;
`

const TestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #f7f9fb;
  border-bottom: 1px solid #d8dee9;

  h3 {
    margin: 0 0 3px;
    font-size: 18px;
    line-height: 24px;
  }

  p {
    margin: 0;
    color: #4b5563;
    font-size: 14px;
    line-height: 20px;
  }

  @media screen and (max-width: 720px) {
    flex-direction: column;
  }
`

const CodeContainer = styled.div`
  .code-block {
    margin: 0;
    border-radius: 0;
  }
`

export const getReproductionTestMarkdown = (code, language) => {
  const fence = getCodeFence(code)
  return `${fence}${getMarkdownLanguage(language)}\n${code}\n${fence}`
}

const ReproductionTest = ({ finding, findingNumber, prefix, reportContent }) => {
  const { code, fileName } = finding.reproductionTest
  const language = getReproductionTestLanguage(fileName, reportContent)

  return (
    <TestContainer id={getReproductionTestId(findingNumber)}>
      <TestHeader>
        <div>
          <h3>
            <FindingIdentifier href={`#${FINDING_HTML_ID_ATTRIBUTE}${findingNumber}`}>
              {prefix}-{findingNumber}
            </FindingIdentifier>{' '}
            {getFindingTitle(finding)}
          </h3>
          <p>
            {fileName ? `File: ${fileName}` : 'Reproduction test'}
            {language ? ` | Language: ${language}` : ''}
          </p>
        </div>
      </TestHeader>
      <CodeContainer>
        <Interweave
          content={convertMdToHtml.makeHtml(getReproductionTestMarkdown(code, language))}
        />
      </CodeContainer>
    </TestContainer>
  )
}

ReproductionTest.propTypes = {
  finding: PropTypes.object,
  findingNumber: PropTypes.number,
  prefix: PropTypes.string,
  reportContent: PropTypes.object,
}

const VulnerabilityReproductionTests = ({ report: { reportContent } }) => {
  const prefix = reportContent.breakdownAndAutomatedAnalysis.vulnIndex || 'QS'

  return (
    <Section title="Vulnerability Reproduction Tests">
      <SectionDescription>
        These tests are designed to be copied into the project&apos;s existing test suite to
        reproduce the associated findings. They can then be retained as regression tests to verify
        that the findings have been remediated and do not recur.
      </SectionDescription>
      {getOrderedFindings(reportContent).map((finding, index) =>
        hasReproductionTest(finding) ? (
          <ReproductionTest
            key={finding.id || index}
            finding={finding}
            findingNumber={index + 1}
            prefix={prefix}
            reportContent={reportContent}
          />
        ) : null,
      )}
    </Section>
  )
}

VulnerabilityReproductionTests.propTypes = {
  report: PropTypes.object,
}

export default VulnerabilityReproductionTests
